import PropTypes from 'prop-types';
import { useMemo, useLayoutEffect } from 'react';
// material
import { CssBaseline } from '@material-ui/core';
import { ThemeProvider, createTheme, StyledEngineProvider } from '@material-ui/core/styles';
// hooks
import useSettings from '../hooks/useSettings';
//
import shape from './shape';
import palette from './palette';
import typography from './typography';
import breakpoints from './breakpoints';
import GlobalStyles from './globalStyles';
import componentsOverride from './overrides';
import shadows, { customShadows } from './shadows';

// ----------------------------------------------------------------------

ThemeConfig.propTypes = {
  children: PropTypes.node
};

export default function ThemeConfig({ children }) {
  const { themeMode, themeDirection } = useSettings();
  const persistedThemeMode = localStorage.getItem('themeMode');
  const mode =
    themeMode === 'light' || themeMode === 'dark'
      ? themeMode
      : persistedThemeMode === 'light' || persistedThemeMode === 'dark'
      ? persistedThemeMode
      : 'dark';
  const isLight = mode === 'light';

  const themeOptions = useMemo(
    () => ({
      palette: isLight ? { ...palette.light, mode } : { ...palette.dark, mode },
      shape,
      typography,
      breakpoints,
      direction: themeDirection,
      shadows: isLight ? shadows.light : shadows.dark,
      customShadows: isLight ? customShadows.light : customShadows.dark
    }),
    [isLight, themeDirection]
  );

  const theme = createTheme(themeOptions);
  // MUI may omit custom palette keys — ensure app chrome tokens exist for CSS variables & sx
  if (!theme.palette.appChrome) {
    theme.palette.appChrome = isLight ? palette.light.appChrome : palette.dark.appChrome;
  }
  theme.components = componentsOverride(theme);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-color-mode', theme.palette.mode);
    const ac = theme.palette.appChrome;
    if (ac) {
      root.style.setProperty('--signwise-app-chrome-main', ac.main);
      root.style.setProperty('--signwise-app-chrome-toolbar', ac.toolbar);
      root.style.setProperty('--signwise-app-chrome-gradient-mid', ac.gradientMid);
    }
  }, [mode]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
